import "./style.css";
import axios from "axios";
import React, {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import * as API_URL from "../../constants/ApiUrl";
import {GRAPHQL, LECTURE_ACTIVATE_UPDATE} from "../../constants/ApiUrl";
import {GET_LECTURE_LIST_QUERY} from "../../constants/query/Lecture.query";
import {LECTURE_DETAIL, LECTURE_LISTENER} from "../../constants/Routes";
import List from "../common/list/List";
import {InitialListProps, ListColumnType, ListProps} from "../common/list/List.props";
import Pagination from "../common/pagination/Pagination";
import Popup from "../common/popup/Popup";
import {PopupProps} from "../common/popup/Popup.props";
import Toast from "../common/toast/Toast";
import * as LECTURE_LIST_PROPS from "./constant/LectureList.props";
import {StatusType} from "./constant/Status.type";
import {LectureDto} from "./dto/Lecture.dto";
import {InitialPaginationProps, PaginationProps} from "../common/pagination/Pagination.props";
import {LectureSearchFormProps} from "./constant/SearchForm.props";
import {Option} from "../common/searchForm/Option";
import SearchForm from "../common/searchForm/SearchForm";
import {saveAs} from "file-saver";
import {SearchFormUtil} from "../common/searchForm/SearchFormUtil";

export default function Lecture() {
    const navigate = useNavigate();
    const [checkedSeqs, setCheckedSeqs] = useState<number[]>([]);

    const [updatedSeq, setUpdatedSeq] = useState<number>(0);
    const [deletedSeq, setDeletedSeq] = useState<number>(0);

    const searchProps = LectureSearchFormProps;
    const [paginationProps, setPaginationProps] = useState<PaginationProps>(InitialPaginationProps);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [listProps, setListProps] = useState<ListProps<LectureDto>>(InitialListProps);

    const [popupProps, setPopupProps] = useState<PopupProps | null>(null);
    const [toastProps, setToastProps] = useState<string[]>([]);

    useEffect(() => {
        setListProps({
            headers: [
                ...LECTURE_LIST_PROPS.HEADERS,
                {
                    type: ListColumnType.TOGGLE_BUTTON,
                    name: "활성화",
                    key: "isActivated",
                    button: {
                        name: "",
                        onClick: (seq: number, isActivated: boolean) => onToggleClick(seq, isActivated)
                    }
                },
                {
                    type: ListColumnType.BUTTON,
                    name: "",
                    key: ["seq", "title", "count"],
                    button: {
                        name: "신청자 목록",
                        onClick: (seq: number, title: string, count: number) => onListenerClick(seq, title, count)
                    }
                },
                {
                    type: ListColumnType.BUTTON,
                    name: "",
                    key: "",
                    button: {
                        name: "상세 보기",
                        onClick: (seq: number) => onDetailClick(seq)
                    }
                }
            ],
            items: [],
            emptyProps: LECTURE_LIST_PROPS.EMPTY_PROPS(() => onDetailClick(0)),
            onCheckedChange: (seqs: number[]) => setCheckedSeqs(seqs)
        });

        setPaginationProps(prevState => ({
            ...prevState,
            onChange: changePage
        }));

        searchProps.onSearch = () => getLectures();
        searchProps.onUpdate = (options: Option[]) => {
            SearchFormUtil.setAllCheckboxesToTrue(options);
            searchProps.options = options;
        }
    }, []);

    const changePage = (page: number) => {
        setPaginationProps(prevState => ({
            ...prevState,
            currentPage: page
        }));
    }

    useEffect(() => {
        getLectures();
    }, [paginationProps.currentPage, deletedSeq]);

    useEffect(() => {
        if (updatedSeq !== 0) {
            axios.put(LECTURE_ACTIVATE_UPDATE(updatedSeq)).then(() => {
                setUpdatedSeq(0);
                getLectures();
            });
        }
    }, [updatedSeq]);

    const getLectures = () => {
        const keyword: string = searchProps.options[1].value;
        const checks: boolean[] = searchProps.options[0].value;
        let status: StatusType = StatusType.ALL;

        if (checks[0] !== checks[1]) {
            status = checks[0] ? StatusType.NOT_EXPIRED : StatusType.EXPIRED;
        }

        axios.post(GRAPHQL, {
            query: GET_LECTURE_LIST_QUERY(status, keyword, paginationProps.currentPage),
        }).then(response => {
            const lectureResponse = response.data.data.getLectures;

            setTotalCount(lectureResponse.totalItems);
            setPaginationProps(prevState => ({
                ...prevState,
                totalPage: lectureResponse.totalPage,
                currentPage: lectureResponse.currentPage
            }));
            setListProps(prevState => ({
                ...prevState,
                items: lectureResponse.items
            }));
        });
    }

    const addToast = (message: string) => {
        setToastProps([...toastProps, message]);
    }

    const handleToastClose = () => {
        setToastProps(toastProps.slice(1));
    }

    const onToggleClick = (seq: number, isActivated: boolean) => {
        if (isActivated) {
            setPopupProps({
                subject: "비활성화 처리하시겠습니까?",
                message1: "비활성화 처리 시 메인에 노출되지 않습니다.",
                message2: null,
                buttonName: "비활성화",
                onConfirm: () => {
                    setPopupProps(null);
                    setUpdatedSeq(seq);
                },
                useCloseButton: true,
                onClose: () => setPopupProps(null)
            });
        } else {
            setUpdatedSeq(seq);
        }
    }

    const onDetailClick = (seq: number) => {
        navigate(LECTURE_DETAIL, {state: {seq: seq}});
    }

    const onListenerClick = (seq: number, title: string, count: number) => {
        navigate(LECTURE_LISTENER, {
            state: {
                seq: seq,
                title: title,
                count: count
            }
        });
    }

    const onDeleteButtonClick = () => {
        let message: string = "";

        if (checkedSeqs.length === 0) {
            message = "삭제할 강의를 선택해 주세요.";
        } else {
            message = "선택한 강의를 삭제하시겠습니까?";
        }

        setPopupProps({
            subject: "강의 삭제",
            message1: message,
            message2: null,
            buttonName: "확인",
            onConfirm: () => {
                setPopupProps(null);

                if (0 < checkedSeqs.length) {
                    deleteLecture();
                }
            },
            useCloseButton: checkedSeqs.length !== 0,
            onClose: () => setPopupProps(null)
        });
    }

    const deleteLecture = () => {
        axios.post(API_URL.LECTURE_DELETE, {seqs: checkedSeqs})
            .then(() => {
                addToast("강의가 삭제되었습니다.");
                setDeletedSeq(checkedSeqs[0]);
            });
    }

    const handleSaveAllButtonClick = () => {
        axios.post(API_URL.EXPORT_LECTURES_TO_EXCEL, [], {responseType: "blob"})
            .then(response => {
                saveAs(response.data, "강의 목록.xlsx");
            });
    }

    const handleSaveButtonClick = () => {
        if (checkedSeqs.length === 0) {
            setPopupProps({
                subject: "강의 목록 엑셀 저장",
                message1: "저장할 항목을 선택해 주세요.",
                message2: null,
                buttonName: "확인",
                onConfirm: () => setPopupProps(null),
                useCloseButton: false,
                onClose: () => {}
            });

            return;
        }

        axios.post(API_URL.EXPORT_LECTURES_TO_EXCEL, checkedSeqs, {responseType: "blob"})
            .then(response => {
                saveAs(response.data, "강의 목록.xlsx");
            });
    }

    return (
        <>
            <div className="lecture-management">
                <main className="wrapper">
                    <div className="lecture-body">
                        <section className="list-search-section">
                            <div className="title">
                                강의 관리
                            </div>
                            <SearchForm {...searchProps}/>
                        </section>
                        <div className="lecture-numbers">
                            <div className="lecture-wrapper">
                                <div className="lecture-all-number" data-testid="total-count">
                                    전체 강의 수 {totalCount}개
                                </div>
                                <div className="lecture-choice">
                                    <button className="save" data-testid="saveAllButton" onClick={handleSaveAllButtonClick}>
                                        전체 엑셀저장
                                    </button>
                                    <button className="save" onClick={handleSaveButtonClick}>
                                        선택항목 엑셀저장
                                    </button>
                                    <button className="delete" onClick={onDeleteButtonClick}>
                                        선택항목 일괄삭제
                                    </button>
                                    <button className="add-button" onClick={() => onDetailClick(0)}>
                                        강의 추가 개설
                                    </button>
                                </div>
                            </div>
                            <List {...listProps}/>
                        </div>
                        <Pagination {...paginationProps}/>
                    </div>
                </main>
            </div>
            {popupProps && <Popup data={popupProps}/>}
            {
                toastProps.map((toastProp, index) => {
                    return (
                        <Toast message={toastProp} onClose={handleToastClose} key={index}/>
                    );
                })
            }
        </>
    )
}